import { AddOutlined, RemoveOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import { TixlyIconButton } from '@tix-ticketing/ui-components';
import { useEffect, useRef, useState } from 'react';
import { DisabledActionWrapper } from 'components/common/disabled-action-wrapper/disabled-action-wrapper';
import { nextAllowedQuantity, previousAllowedQuantity } from 'lib/helpers/quantity-rule-helpers';
interface QuantitySelectorProps {
  minimum?: number | null;
  maximum?: number | null;
  value: number;
  onChange?: (value: number) => void;
  isSmall?: boolean;
  disabled?: boolean;
  /**
   * Sorted list of quantities the customer may select (e.g. from a ticket type's
   * quantity rule). When provided, stepping skips disallowed values and stops
   * at the rule's lowest/highest value. `null` means no restriction.
   */
  allowedValues?: number[] | null;
  /**
   * Called when the customer clicks the (disabled-looking) increase button
   * while it is blocked by `maximum` value.
   */
  onMaxValueReached?: () => void;
  /**
   * Called when the customer clicks an increase/decrease button that is blocked
   * because the next step would violate the quantity rule.
   */
  onQuantityRuleLimit?: () => void;
  /** Accessible label for the increase button while blocked by the max limit. */
  maxReachedLabel?: string;
  /** Accessible label for a button while blocked by the quantity rule. */
  ruleLimitLabel?: string;
  /** Test id applied to the decrease button. */
  decrementTestId?: string;
  /** Test id applied to the increase button. */
  incrementTestId?: string;
}
export function QuantitySelector({
  value,
  maximum,
  minimum,
  isSmall,
  disabled,
  allowedValues,
  onMaxValueReached,
  onQuantityRuleLimit,
  maxReachedLabel,
  ruleLimitLabel,
  decrementTestId,
  incrementTestId,
  onChange
}: QuantitySelectorProps) {
  const min = minimum ?? 0;
  const max = maximum ?? Infinity;
  const allowed = allowedValues && allowedValues.length ? allowedValues : null;
  const nextValue = nextAllowedQuantity(value, allowed);
  const prevValue = previousAllowedQuantity(value, allowed);

  // Increase is blocked either because the rule has no higher value (rule
  // ceiling) or because the next value would exceed the event's total limit.
  const atRuleMax = allowed !== null && nextValue === null;
  const exceedsMax = nextValue !== null && nextValue > max;
  const addDisabled = Boolean(disabled) || atRuleMax || exceedsMax;
  const addBlocked = !disabled && (atRuleMax || exceedsMax);

  // Decrease is blocked at the rule's lowest value (with a rule that doesn't
  // include 0, the type is removed via the order summary instead).
  const atRuleMin = allowed !== null && prevValue === null && value > 0;
  const subtractDisabled = Boolean(disabled) || value <= min || atRuleMin;
  const subtractBlocked = !disabled && atRuleMin;
  const onAddClick = () => {
    if (addDisabled || nextValue === null) return;
    onChange?.(nextValue);
  };
  const onSubtractClick = () => {
    if (subtractDisabled) return;
    onChange?.(prevValue ?? Math.max(min, value - 1));
  };

  // The quantity can also be typed directly: clicking the number turns it into
  // an input so the customer can enter a large amount at once instead of
  // stepping. On commit the typed value is clamped to the allowed range and
  // snapped down to the nearest value the quantity rule permits.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef<HTMLSpanElement>(null);
  // Set when editing is left via the keyboard so focus lands back on the
  // quantity instead of the top of the page. That focus is silent: the
  // quantity reads as plain text again until the customer tabs to it, so
  // finishing an edit leaves no highlight behind.
  const returnFocusToValue = useRef(false);
  const [focusedSilently, setFocusedSilently] = useState(false);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (returnFocusToValue.current) {
      returnFocusToValue.current = false;
      valueRef.current?.focus();
    }
  }, [editing]);
  const startEditing = () => {
    if (disabled) return;
    setDraft(String(value));
    setEditing(true);
  };
  const stopEditingWithKeyboard = (commit: boolean) => {
    returnFocusToValue.current = true;
    setFocusedSilently(true);
    if (commit) commitDraft();else setEditing(false);
  };
  const commitDraft = () => {
    setEditing(false);
    const parsed = parseInt(draft, 10);
    if (Number.isNaN(parsed)) return;
    let target = Math.max(min, parsed);
    if (allowed) {
      const candidates = allowed.filter(a => a <= Math.min(target, max));
      target = candidates.length ? Math.max(...candidates) : allowed[0]!;
      if (parsed > max) onMaxValueReached?.();else if (!allowed.includes(parsed)) onQuantityRuleLimit?.();
    } else if (target > max) {
      target = max;
      onMaxValueReached?.();
    }
    if (target !== value) onChange?.(target);
  };
  return <Box sx={[{
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    width: '100%',
    flexGrow: 0
  }, isSmall ? {
    gap: 0
  } : {
    gap: 2
  }]}>
      <DisabledActionWrapper active={subtractBlocked} onTrigger={() => onQuantityRuleLimit?.()} ariaLabel={ruleLimitLabel ?? ''}>
        <TixlyIconButton data-testid={decrementTestId} variant="outlined" color="monochrome" disabled={subtractDisabled} onClick={onSubtractClick} size={isSmall ? 'small' : 'medium'}>
          <RemoveOutlined />
        </TixlyIconButton>
      </DisabledActionWrapper>
      <Box sx={theme => ({
      display: 'flex',
      width: '44px',
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      ...theme.typography.body1Medium,
      color: value === 0 ? 'text.disabled' : 'text.primary',
      // The quantity reads as plain text until it is hovered or edited,
      // where it takes on the outlined text field look to hint that it
      // can be typed into.
      border: '1px solid transparent',
      borderRadius: `${theme.formItemRadius.main}px`,
      [theme.breakpoints.down('sm')]: {
        borderRadius: `${theme.formItemRadius.mobile}px`
      },
      transition: theme.transitions.create(['border-color', 'color'], {
        duration: theme.transitions.duration.shorter
      }),
      ...(!disabled && (editing ? {
        borderColor: theme.palette.primary.main,
        color: theme.palette.text.primary
      } : {
        '&:hover': {
          borderColor: theme.palette.text.primary,
          color: theme.palette.text.primary
        },
        // Keyboard focus only: clicking the quantity switches to the
        // input, so the focus ring here belongs to tab navigation.
        // It stays off while focus was handed back after an edit.
        ...(focusedSilently ? {} : {
          '&:focus-within': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.text.primary
          }
        })
      }))
    })}>
        {editing ? <Box component="input" ref={inputRef} value={draft} inputMode="numeric" onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value.replace(/\D/g, ''))} onBlur={commitDraft} onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          stopEditingWithKeyboard(true);
        } else if (e.key === 'Escape') {
          // Escape cancels the edit only; without this an enclosing
          // dialog would close along with it.
          e.stopPropagation();
          stopEditingWithKeyboard(false);
        }
      }} sx={{
        width: '100%',
        height: '100%',
        border: 'none',
        outline: 'none',
        background: 'transparent',
        textAlign: 'center',
        font: 'inherit',
        color: 'inherit',
        p: 0,
        MozAppearance: 'textfield'
      }} /> : <Box component="span" ref={valueRef} role="button" tabIndex={disabled ? -1 : 0} aria-disabled={disabled || undefined} onClick={startEditing} onBlur={() => setFocusedSilently(false)} onKeyDown={(e: React.KeyboardEvent<HTMLSpanElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          startEditing();
        }
      }} sx={{
        width: '100%',
        textAlign: 'center',
        cursor: disabled ? 'default' : 'pointer',
        outline: 'none'
      }}>
            {value}
          </Box>}
      </Box>
      <DisabledActionWrapper active={addBlocked} onTrigger={() => atRuleMax ? onQuantityRuleLimit?.() : onMaxValueReached?.()} ariaLabel={(atRuleMax ? ruleLimitLabel : maxReachedLabel) ?? ''}>
        <TixlyIconButton data-testid={incrementTestId} variant="outlined" color="monochrome" disabled={addDisabled} onClick={onAddClick} size={isSmall ? 'small' : 'medium'}>
          <AddOutlined />
        </TixlyIconButton>
      </DisabledActionWrapper>
    </Box>;
}